<template>
	<v-hover v-slot="{ hover }">
		<div
			class="file-upload-overlay pt-2 pr-2"
			:class="{ 'overlay-enabled': hover }"
		>
			<v-file-input
				ref="fileInput"
				:model-value="modelValue"
				:disabled="disabled"
				:error="error"
				:hint="hint"
				:placeholder="placeholder"
				clearable
				color="secondary"
				label="Upload files"
				prepend-icon="mdi-paperclip"
				counter
				multiple
				variant="outlined"
				persistent-hint
				@update:model-value="onPick"
				@click:clear="onClear"
			>
				<template #selection>
					<draggable
						:model-value="modelValue"
						:item-key="getItemKey"
						:animation="150"
						handle=".chip-drag-handle"
						:force-fallback="true"
						:fallback-on-body="true"
						@update:model-value="onReorder"
					>
						<template #item="{ element, index }">
							<v-chip
								class="bg-secondary chip-ellipsis"
								closable
								@click:close="removeFile(index)"
							>
								<v-icon
									size="16"
									class="mr-1 chip-drag-handle"
									icon="mdi-drag"
								/>
								{{ truncateText(displayName(element), 50) }}
							</v-chip>
						</template>
					</draggable>
				</template>
			</v-file-input>
		</div>
	</v-hover>
</template>

<script>
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import utils from "@/utils";
import draggable from "vuedraggable";

export default {
	name: "FileUpload",
	components: {
		draggable,
	},

	props: {
		disabled: {
			type: Boolean,
			default: false,
		},
		error: {
			type: Boolean,
			default: false,
		},
		success: {
			type: Boolean,
			default: false,
		},
		modelValue: { type: Array, default: () => [] },
	},

	emits: ["removed", "update:modelValue"],

	data() {
		return {
			hint: "Click above or drag and drop to upload files.  Include all associated files, such as supplements.  Removed files are not deleted from Dactyl.",
			placeholder: "Drag files here or click here to select files",
			overlay: false,
			removedFiles: [],
		};
	},

	watch: {
		error(val) {
			this.delayedReset(val);
		},
		success(val) {
			this.delayedReset(val);
		},
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),
		truncateText(text) {
			return utils.truncateText(text);
		},
		getItemKey(file) {
			// Use uuid if available, otherwise create a unique key from file properties
			return file.uuid || `${file.name}-${file.size}-${file.lastModified}`;
		},
		displayName(file) {
			// Return the file name for display
			return file.name || "Unknown file";
		},
		removeFile(index) {
			const file = this.modelValue[index];
			if (file && file.uuid) {
				this.$emit("removed", file);
			}
			const updated = this.modelValue.filter((_, i) => i !== index);

			// Emit the updated list so indices naturally re-start at 0..n-1
			this.$emit("update:modelValue", updated);
		},
		delayedReset(flag) {
			if (flag === true) {
				setTimeout(() => (flag = false), 1000);
			}
		},
		onClear() {
			this.$emit(
				"removed",
				this.modelValue.filter((file) => file.uuid),
			);
			this.$emit("update:modelValue", []);
		},
		// The file input replaces its native FileList on every pick, so a fresh
		// pick emits only the newly selected files. Accumulate them across batches
		// instead of replacing, appending only files not already tracked (a
		// reference check - guards against a re-emit of the full model). This is
		// the file-selection action; reordering is handled separately by onReorder,
		// so neither path has to guess the user's intent from an array diff.
		onPick(picked) {
			const list = picked || [];
			if (!list.length) {
				this.$emit("update:modelValue", []);
				return;
			}
			const additions = list.filter((file) => !this.modelValue.includes(file));
			if (additions.length) {
				this.$emit("update:modelValue", [...this.modelValue, ...additions]);
			}
		},
		// Drag-reorder emits the same files in a new order; pass it straight
		// through as the new model.
		onReorder(reordered) {
			this.$emit("update:modelValue", reordered || []);
		},
	},
};
</script>

<style scoped lang="scss">
.file-upload-overlay {
	position: relative;
	background: rgba($color-dactyl-orange, 0);
	border-radius: 5px;

	&.overlay-enabled {
		background: rgba($color-dactyl-orange, 0.3);
		outline: 3px dashed $color-dactyl-orange;
	}

	& .overlay-text {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		color: $color-dactyl-blue;
		opacity: 0;

		&.overlay-enabled {
			opacity: 1;
			z-index: 100;
		}
	}
}

:deep(.chip-ellipsis) {
	z-index: 999;
	.v-chip__content {
		z-index: 999;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
}

:deep(.v-field__input) {
	z-index: 2;
}

.chip-drag-handle {
	cursor: grab;
}

.chip-drag-handle:active {
	cursor: grabbing;
}
</style>
